import { User } from "../gtm/User";
import { makeJsonRequest } from "../utils/JsonRequests";
import { HttpMethods } from "../utils/HttpMethods";
import { UieCurrentUserResponse } from "./dtos/UieCurrentUserResponse";
import "../global";
import { inject, injectable, singleton } from "tsyringe";
import { ServiceTokens } from "../shared/ServiceTokens";
import { SiteSettings } from "../shared/SiteSettings";
import { Logger } from "../utils/Logger";
import { getCookie } from "typescript-cookie";
import { CookiesNames } from "../gtm/Cookies";
import { setOneYearCookie } from "../utils/Cookies";
import { UieLoginRequest } from "./dtos/UieLoginRequest";
import { UieLoginResponse } from "./dtos/UieLoginResponse";

/**
 *  This class is used to simplify interactions with CBC's UIE system.
 *  */
@injectable()
@singleton()
export class UieClient {
  constructor(@inject(ServiceTokens.SiteSettings) private siteSettings: SiteSettings) {}

  private get baseUrl(): string {
    return this.siteSettings.UieBaseAddress;
  }

  private get currentUserUrl(): string {
    return `${this.baseUrl}/v0/current_user`;
  }

  private get logoutUrl(): string {
    return `${this.baseUrl}/v0/logout`;
  }

  private get loginUrl(): string {
    return `${this.baseUrl}/v0/login`;
  }

  /**
   * Fetch a RC_Plus ID from the UIE based on either the identityProviderUserId or
   * the viafouraId of the user.
   * */
  public async fetchRcPlusId(user: User): Promise<string | undefined> {
    const identityProviderUserId = user.identityProviderUserId;
    const viafouraId = user.viafouraId ? user.viafouraId : user.identityProviderUserId;

    if (identityProviderUserId) {
      const uieLoginRequest: UieLoginRequest = {
        "@context": {
          cbc: "http://vocab.data.cbc.ca/",
        },
        "cbc:loginradius_id": ["" + identityProviderUserId + ""],
        "cbc:viafoura_id": ["" + viafouraId + ""],
      };

      const response = await makeJsonRequest(this.loginUrl, HttpMethods.POST, "include", uieLoginRequest);
      if (!response.ok) {
        await Logger.requestError("Could not fetch RC_Plus_ID from UIE login endpoint.", response);
        return;
      }
      const loginResponse = (await response.json()) as UieLoginResponse;
      if (loginResponse["cbc:rc_plus_id"] && loginResponse["cbc:rc_plus_id"].length > 0) {
        return loginResponse["cbc:rc_plus_id"][0];
      } else {
        await Logger.requestError("UIE response didn't include a rc_plus_id", response);
      }
    } else {
      const errorMsg = "Cannot call UIE login because the identityProviderUserId is undefined";
      Logger.error(errorMsg);
    }
    return;
  }

  /**
   * Retrieves the RCVisitorId while handling the associated cookies.
   * If the user is still logged in, it logs them out.
   * Utilizes the rcVisitorBackup cookie if available and updates the cookie values accordingly.
   */
  public async handleRcVisitorId(): Promise<string | undefined> {
    // An anonymous user should have a rc_visitor identifier, not a rc_plus identifier.
    let rcPlus = getCookie(CookiesNames.rcPlus);
    if (rcPlus) {
      Logger.log("Logging out user from UIE. rc_plus:", rcPlus);
      await this.logoutUser();
      rcPlus = getCookie(CookiesNames.rcPlus);
      if (rcPlus) {
        Logger.warn("Logging out from UIE didn't delete the rcPlus cookie.");
      }
    }

    let rcVisitorId = getCookie(CookiesNames.rcVisitorBackup);
    if (rcVisitorId) {
      setOneYearCookie(CookiesNames.rcVisitor, rcVisitorId);
      setOneYearCookie(CookiesNames.rcVisitorBackup, rcVisitorId);
    } else {
      rcVisitorId = getCookie(CookiesNames.rcVisitor);
    }
    if (!rcVisitorId) {
      Logger.verbose("Fetching rc_visitor");
      rcVisitorId = await this.fetchRcVisitorId();
      if (!rcVisitorId) {
        Logger.error("Failed to get a RCVisitorId from UIE");
        return;
      } else {
        setOneYearCookie(CookiesNames.rcVisitorBackup, rcVisitorId);
        setOneYearCookie(CookiesNames.rcVisitor, rcVisitorId);
      }
    }
    return rcVisitorId;
  }

  private async fetchRcVisitorId(): Promise<string | undefined> {
    const response = await makeJsonRequest(this.currentUserUrl, HttpMethods.GET, "include");
    if (!response.ok) {
      await Logger.requestError("Could not fetch rc_visitor_id from UIE current user endpoint.", response);
      return;
    }
    const currentUserResponse = (await response.json()) as UieCurrentUserResponse;
    if (currentUserResponse["cbc:rc_visitor_id"] && currentUserResponse["cbc:rc_visitor_id"].length > 0) {
      return currentUserResponse["cbc:rc_visitor_id"][0];
    } else {
      await Logger.requestError("UIE response didn't include a rc_visitor_id", response);
    }
  }

  public async logoutUser(): Promise<Response> {
    try {
      return await makeJsonRequest(this.logoutUrl, HttpMethods.POST, "include");
    } catch (e) {
      Logger.error("Failed to logout user.");
      throw e;
    }
  }
}
